<template>
  <template v-if="errorStore.isError">
    <error-page :error="{ statusCode: errorStore.statusCode }" />
  </template>
  <template v-else>
    <notifications
      v-for="(notificationType, index) in Object.values(NotificationType)"
      :key="index"
      :width="isMobileView ? '90%' : '25rem'"
      :group="notificationType"
    >
      <template #body="{ item, close }">
        <notification
          :notification="item"
          :type="notificationType"
          @close="close"
        />
      </template>
    </notifications>
    <router-view #="{ Component, route }">
      <suspense timeout="0">
        <component :is="Component" />

        <template #fallback>
          <loading-modal />
        </template>
      </suspense>
    </router-view>
    <DynamicModalContainer />
    <div v-if="loaderStore.isLoading">
      <loading-modal />
    </div>
  </template>
</template>

<script setup lang="ts">
import { onErrorCaptured, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useErrorStore } from '@/store/use-error-store';
import { useLoaderStore } from '@/store/use-loader-store';
import { LoadingModal } from '@/components/base/modals/loading-modal';
import { ErrorPage } from '@/views/_components/error-page';
import { Notification, NotificationType } from '@/components/base/notification';
import { useWindowWidth } from '@/composables/use-window-width';
import { DynamicModalContainer } from '@/components/base/modals/dynamic-modal-container';
import { apiRetrieveActiveCookieConsentBanner } from '@/api/organizations';
import { useAuthUserStore } from '@/store/use-auth-user-store';
import { useCurrentOrganizationStore } from '@/store/use-current-organization-store';
import { storeToRefs } from 'pinia';
import { captureHandledError } from '@/constants/sentry';

let bannerLoaded = false;
const injectedElements: (HTMLScriptElement | Node)[] = [];

const authUserStore = useAuthUserStore();
const { authUser } = storeToRefs(authUserStore);

const currentOrganizationStore = useCurrentOrganizationStore();
const { currentOrganization } = storeToRefs(currentOrganizationStore);

const loadCookieConsentBanner = async () => {
  if (globalThis.window === undefined || !document) {
    return;
  }

  if (!currentOrganization.value?.isCookieConsentEnabled) {
    for (const element of injectedElements) {
      if (element.parentNode) {
        element.remove();
      }
    }
    injectedElements.length = 0;
    bannerLoaded = false;
    return;
  }

  if (bannerLoaded) {
    return;
  }

  for (const element of injectedElements) {
    if (element.parentNode) {
      element.remove();
    }
  }
  injectedElements.length = 0;

  try {
    const response = await apiRetrieveActiveCookieConsentBanner();
    if (!response?.activeBanner?.script) return;

    bannerLoaded = true;

    const scriptHtml = response.activeBanner.script;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = scriptHtml;

    const noscriptElements = tempDiv.querySelectorAll('noscript');
    for (const noscript of noscriptElements) noscript.remove();

    const scripts = [...tempDiv.querySelectorAll('script')];
    const scriptPromises: Promise<void>[] = [];

    for (const script of scripts) {
      if (script.textContent && !script.src) {
        const scriptEl = document.createElement('script');
        if (script.type) scriptEl.type = script.type;
        scriptEl.textContent = script.textContent;
        document.body.append(scriptEl);
        injectedElements.push(scriptEl);
        script.remove();
      }
    }

    for (const script of scripts) {
      if (script.src) {
        const promise = new Promise<void>((resolve) => {
          const scriptEl = document.createElement('script');
          scriptEl.src = script.src;
          if (script.type) scriptEl.type = script.type;
          if (script.charset) scriptEl.charset = script.charset;
          scriptEl.async = script.async || false;

          for (const attr of script.attributes) {
            if (attr.name.startsWith('data-')) {
              scriptEl.setAttribute(attr.name, attr.value);
            }
          }

          scriptEl.addEventListener('load', () => resolve());
          scriptEl.addEventListener('error', () => resolve());

          document.body.append(scriptEl);
          injectedElements.push(scriptEl);
        });
        scriptPromises.push(promise);
        script.remove();
      }
    }

    while (tempDiv.firstChild) {
      const node = tempDiv.firstChild;
      document.body.append(node);
      injectedElements.push(node);
    }

    await Promise.all(scriptPromises);
  } catch (err) {
    const isAuthError =
      err &&
      typeof err === 'object' &&
      'response' in err &&
      err.response &&
      typeof err.response === 'object' &&
      'status' in err.response &&
      (err.response.status === 401 || err.response.status === 403);

    if (!isAuthError) {
      captureHandledError(err, { feature: 'cookie-consent-banner' });
    }
  }
};

watch(
  () => [authUser.value, currentOrganization.value],
  async ([newAuthUser, newOrg]) => {
    if (
      globalThis.window !== undefined &&
      newAuthUser &&
      newOrg &&
      !bannerLoaded
    ) {
      await loadCookieConsentBanner();
    }
  },
  { immediate: globalThis.window !== undefined },
);

watch(
  () => currentOrganization.value,
  async (newOrg, oldOrg) => {
    if (
      globalThis.window !== undefined &&
      newOrg &&
      oldOrg &&
      newOrg.id !== oldOrg.id
    ) {
      bannerLoaded = false;
      await loadCookieConsentBanner();
    }
  },
);

onMounted(async () => {
  if (globalThis.window !== undefined) {
    await loadCookieConsentBanner();
  }
});

const router = useRouter();

const errorStore = useErrorStore();

onErrorCaptured<unknown>((err) => {
  errorStore.handleError(router, err);

  return true;
});

const loaderStore = useLoaderStore();
const { isMobileView } = useWindowWidth();
</script>
